import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Alert, Spinner } from "react-bootstrap";
import { FaUsers, FaRegCopy, FaCheck, FaEdit } from "react-icons/fa";
import ActivationLinkActions from "./ActivationLinkActions";
import ActivationCountdown from "./ActivationCountdown";
import UserAvatar, { __setUserAvatarInfoSource } from "./UserAvatar";
import RoleSummaryCard, { __setRoleSummaryCardInfoSource } from "./RoleSummaryCard";
import ErrorAlert from "./ErrorAlert";
import { getShareMessageByLang } from "./shareMessageTranslations";
import "./CardUserActivationModal.css";

let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["cardUserActivationModal"]?.[key] || fallback;

// ---------------------------------------------------------------------------
// CopyEmailButton — small clipboard icon next to the email, same
// navigator.clipboard.writeText + 2s "copied" feedback pattern as
// ShareButton's copy-link button (mission: no re-invented copy logic).
// ---------------------------------------------------------------------------
const CopyEmailButton = ({ email }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyEmail = async (e) => {
    e?.stopPropagation();
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Clipboard indisponible (permission refusée, contexte non sécurisé…) —
      // l'email reste visible et copiable manuellement.
    }
  };

  return (
    <button
      type="button"
      className="carduser-activation-email-copy-btn"
      onClick={handleCopyEmail}
      aria-label={copied ? __elakayText("t031", "Email copied!") : __elakayText("t032", "Copy email")}
      title={copied ? __elakayText("t031", "Email copied!") : __elakayText("t032", "Copy email")}
    >
      {copied ? <FaCheck /> : <FaRegCopy />}
    </button>
  );
};

// ---------------------------------------------------------------------------
// UserIdentityHeader — avatar (shared UserAvatar, same one CardUser's card
// itself uses), name, email, role badge, team count.
// ---------------------------------------------------------------------------
const UserIdentityHeader = ({ userId, fullName, email, roleName, numberOfTeamMembers, statusBadge }) => (
  <div className="carduser-activation-header">
    <UserAvatar userId={userId} fullName={fullName} className="carduser-activation-avatar" />
    <div className="carduser-activation-identity">
      <h3 className="carduser-activation-name">{fullName}</h3>
      {email && (
        <p className="carduser-activation-email">
          {email}
          <CopyEmailButton email={email} />
        </p>
      )}
      <div className="carduser-activation-role">
        {roleName && <span className="carduser-activation-role-badge">{roleName}</span>}
        {statusBadge}
        {Number.isFinite(numberOfTeamMembers) && (
          <span className="carduser-activation-team-count">
            <FaUsers /> {numberOfTeamMembers}
          </span>
        )}
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// EditCapacityButton — opens EditRoleCapacityModal (rendered by CardUser
// alongside this modal, not inside it -- separate modal, per spec) to edit
// this user's team-managing limits (max Royalty/Seller/Producer/Associate/
// Investment/Admin) and expiration, the same fields addAdminFamily.jsx
// exposes when adding/editing a role. Only shown when the caller has already
// determined the acting admin is allowed to manage this role tier
// (canEditCapacity) -- never rendered unconditionally.
// ---------------------------------------------------------------------------
const EditCapacityButton = ({ canEditCapacity, onEditCapacity }) => {
  if (!canEditCapacity || !onEditCapacity) return null;
  return (
    <button
      type="button"
      className="carduser-activation-edit-capacity-btn"
      onClick={onEditCapacity}
    >
      <FaEdit /> {__elakayText("t041", "Edit role limits")}
    </button>
  );
};

// ---------------------------------------------------------------------------
// ActivationStatusBadge — derived purely from real data (never a guess).
// ---------------------------------------------------------------------------
const ActivationStatusBadge = ({ status }) => {
  const labels = {
    pending: __elakayText("t002", "Invitation pending"),
    expired: __elakayText("t003", "Invitation expired"),
    activated: __elakayText("t004", "User activated"),
    "role-pending": __elakayText("t005", "Role pending"),
    "role-accepted": __elakayText("t006", "Role accepted"),
  };
  if (!status || !labels[status]) return null;
  return <span className={`carduser-activation-status carduser-activation-status--${status}`}>{labels[status]}</span>;
};

// ---------------------------------------------------------------------------
// ActivationStatusMessage — the loading/already-used/expired/no-link states
// that gate whether ActivationLinkActions (Share/Copy/QR — the one shared
// component, also used by addAdminFamily) is even shown. Never the raw
// token as text.
// ---------------------------------------------------------------------------
const ActivationStatusMessage = ({
  infoSource,
  shareUrl,
  loading,
  activator,
  userInfo,
  fullName,
  roleName,
  expired,
  onSuccess,
  onError,
  onExpire,
}) => {
  const activationAlreadyCompleted = Boolean(activator?.ativated);

  return (
    <div className="carduser-share-section">
      <h5>{__elakayText("t017", "Activation link")}</h5>

      {loading && (
        <div className="carduser-share-loading">
          <Spinner animation="border" size="sm" /> {__elakayText("t018", "Loading link…")}
        </div>
      )}

      {!loading && activationAlreadyCompleted && (
        <Alert variant="secondary" className="mb-0">
          {__elakayText("t019", "This invitation has already been used.")}
        </Alert>
      )}

      {/* One canonical timer (mission: never a forked/re-implemented timer
          per page) -- built off the fetched (correctly-selected) ativator
          entry's own experyDate, never a role date, never createdAt. Ticks
          every second and shows its own "expired" message once it crosses
          zero, without a page reload -- onExpire bubbles that moment up to
          the modal so ActivationLinkActions/status badge/delete action all
          flip in sync with it. */}
      {!loading && !activationAlreadyCompleted && activator?.experyDate && (
        <div className="carduser-share-countdown">
          <ActivationCountdown infoSource={infoSource} expiresAt={activator.experyDate} onExpire={onExpire} />
        </div>
      )}

      {!loading && !activationAlreadyCompleted && !expired && (
        <ActivationLinkActions
          infoSource={infoSource}
          activationUrl={shareUrl}
          user={userInfo}
          fullName={fullName}
          roleName={roleName}
          // Same localized invitation paragraph addAdminFamily's
          // SuccessResultCard already sends -- without it,
          // ActivationLinkActions falls back to a bare "fullName —
          // roleName" line with no context at all (mission: the share
          // message must always explain what the link is for).
          shareMessage={getShareMessageByLang(infoSource?.languageAPI?.language)}
          expirationDate={activator?.experyDate}
          onSuccess={onSuccess}
          onError={onError}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// ActivityDateSection — explicit, guarded dates (never DateFromTimestamp on
// an absent/invalid value -- mission section 11).
// ---------------------------------------------------------------------------
const formatDateOrPlaceholder = (timestamp, placeholder) => {
  const numeric = Number(timestamp);
  if (!timestamp || !Number.isFinite(numeric)) return placeholder;
  const date = new Date(numeric);
  if (Number.isNaN(date.getTime())) return placeholder;
  return date.toLocaleString("en-US", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false, timeZone: "America/New_York",
  });
};

const ActivityDateSection = ({ startDate, endDate }) => {
  const placeholder = __elakayText("t024", "—");
  return (
    <div className="carduser-activity-section">
      <h5>{__elakayText("t025", "Activity dates")}</h5>
      <table className="carduser-activity-table">
        <thead>
          <tr>
            <th>{__elakayText("t026", "Start date")}</th>
            <th>{__elakayText("t027", "Expiration date")}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{formatDateOrPlaceholder(startDate, placeholder)}</td>
            <td>{formatDateOrPlaceholder(endDate, placeholder)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ExpiredInvitationActions — delete, only when the business rule allows it.
// ---------------------------------------------------------------------------
const ExpiredInvitationActions = ({ canDelete, deleteLoading, onDelete }) => {
  if (!canDelete) return null;
  return (
    <div className="carduser-modal-footer-actions">
      <button
        type="button"
        className="btn btn-outline-danger btn-sm"
        disabled={deleteLoading}
        onClick={onDelete}
      >
        {deleteLoading ? __elakayText("t028", "Deleting…") : __elakayText("t029", "Delete expired invitation")}
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// CardUserActivationModal — the composed modal (replaces infonLinkAdd's old
// inline-styled implementation entirely).
// ---------------------------------------------------------------------------
const CardUserActivationModal = (props) => {
  const {
  show,
  onHide,
  infoSource,
  userInfo,
  userId,
  fullName,
  email,
  roleName,
  numberOfTeamMembers,
  oldRoleData,
  newRoleData,
  userAgree,
  activator,
  shareUrl,
  shareLoading = false,
  roleStartDate,
  roleEndDate,
  error,
  success,
  onDismissError = () => {},
  onDismissSuccess = () => {},
  onShareCopied,
  onShareError,
  canDelete = false,
  deleteLoading = false,
  onDelete,
  canEditCapacity = false,
  onEditCapacity,
} = props;
  __setElakayInfoSource(infoSource);
  __setUserAvatarInfoSource(infoSource);
  __setRoleSummaryCardInfoSource(infoSource);

  const activationAlreadyCompleted = Boolean(activator?.ativated);
  // Initial read (covers the case where the fetched entry was already past
  // its experyDate before the countdown ever mounts) OR'd with the live flag
  // the ActivationCountdown flips the instant it crosses zero while the
  // modal stays open -- so this never requires a page reload to reflect
  // expiry (mission section 9).
  const initiallyExpired = activator?.experyDate ? new Date(activator.experyDate).getTime() <= Date.now() : false;
  const [liveExpired, setLiveExpired] = useState(false);
  // Reset whenever a different token is loaded (e.g. re-opening the modal
  // for another user, or the on-demand fetch resolving) so a stale expiry
  // from a previous token never leaks onto the new one.
  React.useEffect(() => { setLiveExpired(false); }, [activator?.token]);
  const activationExpired = initiallyExpired || liveExpired;

  // mission section 5: status determined from real data, never just the raw
  // `activator` boolean without knowing what it represents.
  const status = activationAlreadyCompleted
    ? "activated"
    : activationExpired
      ? "expired"
      : shareUrl
        ? "pending"
        : userAgree
          ? "role-accepted"
          : "role-pending";

  return (
    <Modal
      show={show}
      onHide={onHide}
      dialogClassName="carduser-activation-modal"
      centered
      size="lg"
      scrollable
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title>{__elakayText("t030", "User activation")}</Modal.Title>
      </Modal.Header>

      <Modal.Body className="px-3 px-md-4 pb-4">
        <UserIdentityHeader
          userId={userId}
          fullName={fullName}
          email={email}
          roleName={roleName}
          numberOfTeamMembers={numberOfTeamMembers}
          statusBadge={<ActivationStatusBadge status={status} />}
        />

        <EditCapacityButton canEditCapacity={canEditCapacity} onEditCapacity={onEditCapacity} />

        {(oldRoleData || newRoleData) && (
          <div className="carduser-role-comparison">
            {!userAgree && oldRoleData && <RoleSummaryCard variant="previous" roleData={oldRoleData} />}
            {newRoleData && <RoleSummaryCard variant="requested" roleData={newRoleData} />}
          </div>
        )}

        <ActivationStatusMessage
          infoSource={infoSource}
          shareUrl={shareUrl}
          loading={shareLoading}
          activator={activator}
          userInfo={userInfo}
          fullName={fullName}
          roleName={roleName}
          expired={activationExpired}
          onSuccess={onShareCopied}
          onError={onShareError}
          onExpire={() => setLiveExpired(true)}
        />

        {/* Falls back to the pending invitation's own dates (activator --
            startDate derived server-side from its ativator entry's ObjectId
            creation timestamp, experyDate its real expiry) when the role
            itself has no start/end date yet -- e.g. right after
            addAdminFamily grants/updates a role and the invite is still
            awaiting acceptance, matching the "Expires on" date already shown
            in SuccessResultCard instead of a bare "—". */}
        <ActivityDateSection
          startDate={roleStartDate || (activator?.startDate ? new Date(activator.startDate).getTime() : null)}
          endDate={roleEndDate || (activator?.experyDate ? new Date(activator.experyDate).getTime() : null)}
        />

        <ErrorAlert
          error={error}
          success={success}
          setError={onDismissError}
          setSuccess={onDismissSuccess}
          duration={10000}
        />

        <ExpiredInvitationActions canDelete={canDelete} deleteLoading={deleteLoading} onDelete={onDelete} />
      </Modal.Body>
    </Modal>
  );
};

export default CardUserActivationModal;
export {
  UserIdentityHeader,
  ActivationStatusBadge,
  RoleSummaryCard,
  ActivationStatusMessage,
  ActivityDateSection,
  ExpiredInvitationActions,
  EditCapacityButton,
};
